import { createContext, useCallback, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAsyncEffect, useAsyncState, wrap } from '@hilma/tools';
import { createTheme } from '@material-ui/core/styles';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import StylesProvider from '@material-ui/styles/StylesProvider';
import jssPreset from '@material-ui/styles/jssPreset';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import heLocale from 'date-fns/locale/he';
import enLocale from 'date-fns/locale/en-US';
import { create } from 'jss';
import rtl from 'jss-rtl';

import { TranslatedI18n, I18n, I18nSelector, I18nText, Language, Localize } from '../types';
import { green, lightGreen, error, i18n as baseI18n } from '../constants';
import { usePathLanguage } from '../hooks';
import { isLanguage } from '../functions';
import { languages } from '../constants';

export const LanguageContext = createContext<Language | null>(null);
export const TranslateContext = createContext<((key: string | I18nSelector) => string) | null>(null);
export const TransformObjectContext = createContext<(<T>(object: Localize<T>) => T) | null>(null);
export const TranslatedI18nContext = createContext<TranslatedI18n | null>(null);
export const IsI18nRouterContext = createContext<boolean | null>(null);

export interface I18nProviderProps {
    i18n?: Record<string, I18nText<any>>;
    router?: boolean;
    muiPalette?: any
}

const I18nProvider: React.FC<I18nProviderProps> = ({ i18n = baseI18n, router = true, muiPalette, children }) => {
    const pathLanguage = usePathLanguage();

    const [language, setLanguage] = useAsyncState<Language>(() => {
        let language: string | null = pathLanguage || localStorage.getItem('language');
        if (!isLanguage(language)) language = Language.He;
        localStorage.setItem('language', language as string);
        return router ? language as Language : Language.He;
    });

    const theme = useMemo(() => createTheme({
        direction: language === Language.He ? 'rtl' : 'ltr',
        palette: muiPalette || {
            primary: {
                main: green
            },
            secondary: {
                main: lightGreen
            },
            error: {
                main: error
            }
        }
    }), [language])

    const jss = useMemo(() => language === Language.He ? create({ plugins: [...jssPreset().plugins, rtl()] }) : create({ plugins: [...jssPreset().plugins] }), [language])

    const translatedI18n = useMemo<TranslatedI18n>(() => {
        const translatedI18n = {} as any;

        for (const key in i18n) {
            if (Object.prototype.hasOwnProperty.call(i18n, key)) {
                translatedI18n[key] = i18n[key as keyof I18n][language];
            }
        }

        return translatedI18n;
    }, [language]);

    const translate = useCallback((key: string | I18nSelector): string => {
        try {
            if (typeof key === "function") return key(translatedI18n);

            const [mainKey, secondaryKey, ...keys] = key.split('.');
            const mainResource: any = i18n[mainKey as keyof I18n][language];

            const text = keys.reduce((previousValue, currentValue) => {
                return previousValue[currentValue];
            }, mainResource[secondaryKey as keyof I18n]);

            return text;
        } catch (error) {
            return '';
        }
    }, [language, translatedI18n]);

    const transformObject = useCallback((object: any): any => {
        const newObject: Record<string, any> = {};
        for (const key in object) {
            const keyLanguage = key.substring(0, 2);
            if (!isLanguage(keyLanguage)) {
                newObject[key] = object[key];
                continue;
            }
            if (keyLanguage === language && key.charAt(2) && key.charAt(2) === key.charAt(2).toUpperCase()) newObject[`${key.charAt(2).toLowerCase()}${key.slice(3)}`] = object[key];
        }
        return newObject;
    }, [language]);

    useAsyncEffect(async () => {
        if (!router) return;

        const newLanguage = pathLanguage || language;

        if (newLanguage && (newLanguage === Language.En || newLanguage === Language.He)) {
            await setLanguage(newLanguage);
            localStorage.setItem('language', newLanguage);
        } else {
            const newLanguage = await setLanguage(language => {
                if (language === Language.En) return Language.He;
                return Language.En;
            });
            localStorage.setItem('language', newLanguage);
        }
    }, [pathLanguage]);

    const wrapper = wrap(
        [ThemeProvider, { theme }],
        [StylesProvider, { jss }],
        [MuiPickersUtilsProvider, { utils: DateFnsUtils, locale: language === Language.He ? heLocale : enLocale }],
        [LanguageContext.Provider, { value: language }],
        [TranslateContext.Provider, { value: translate }],
        [TransformObjectContext.Provider, { value: transformObject }],
        [TranslatedI18nContext.Provider, { value: translatedI18n }],
        [IsI18nRouterContext.Provider, { value: router }]
    );

    if (!router) return wrapper(children) as React.ReactElement;

    return wrapper(
        <Routes>
            <Route path={"/"} element={<Navigate to={`/${language}/`} />} />
            {
                languages.map((language: string) => (
                    <Route key={language} path={`/${language}/*`} element={children}
                    />
                ))}
            {
                languages.map((language: string) => (
                    <Route key={language} path={`/${language}/`} element={children}
                    />
                ))}
            <Route path="*" element={<Navigate to={`/${language}/page-not-found`} />} />
        </Routes>
    ) as React.ReactElement;
}

export default I18nProvider;